// components/HeaderNews.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  FaHome,
  FaFire,
  FaNewspaper,
  FaBuilding,
  FaBookmark,
  FaSearch,
  FaBell,
  FaUser,
  FaBars,
  FaTimes,
  FaMoon,
  FaSun,
  FaChevronDown,
  FaSignInAlt,
  FaUserPlus,
  FaCog,
  FaSignOutAlt,
  FaBookOpen,
  FaHistory
} from 'react-icons/fa';
import { RoundedImage } from '../components/RoundedImage';

interface NavLink {
  label: string;
  target: string;
  icon: string;
  active?: boolean;
}

interface HeaderNewsProps {
  siteName: string;
  logo?: string;
  navLinks: NavLink[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  onSearch: (query: string) => void;
  onToggleTheme: () => void;
  isDarkMode: boolean;
  showSearch: boolean;
  onToggleSearch: () => void;
  userMenu?: {
    showLogin: boolean;
    showRegister: boolean;
    showProfile: boolean;
    profileUrl: string;
  };
  userAvatar?: string;
  userName?: string;
  notificationCount?: number;
  bookmarksCount?: number;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  FaHome: <FaHome />,
  FaFire: <FaFire />,
  FaNewspaper: <FaNewspaper />,
  FaBuilding: <FaBuilding />,
  FaBookmark: <FaBookmark />,
  FaSearch: <FaSearch />,
  FaBell: <FaBell />,
  FaUser: <FaUser />,
  FaBars: <FaBars />,
  FaTimes: <FaTimes />,
  FaMoon: <FaMoon />,
  FaSun: <FaSun />,
  FaChevronDown: <FaChevronDown />,
  FaSignInAlt: <FaSignInAlt />,
  FaUserPlus: <FaUserPlus />,
  FaCog: <FaCog />,
  FaSignOutAlt: <FaSignOutAlt />,
  FaBookOpen: <FaBookOpen />,
  FaHistory: <FaHistory />
};

const getIcon = (iconName: string): React.ReactNode => {
  return ICON_MAP[iconName] || <FaHome />;
};

export default function HeaderNews({
  siteName,
  logo,
  navLinks,
  activeTab,
  onTabChange,
  onSearch,
  onToggleTheme,
  isDarkMode,
  showSearch,
  onToggleSearch,
  userMenu = { showLogin: true, showRegister: true, showProfile: true, profileUrl: '/profile' },
  userAvatar,
  userName = 'Guest',
  notificationCount = 0,
  bookmarksCount = 0
}: HeaderNewsProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [notifications] = useState([
    { id: 1, title: 'Breaking News: Major Story', time: '2 min ago', read: false },
    { id: 2, title: 'New article published', time: '15 min ago', read: false },
    { id: 3, title: 'Your article was shared 10 times', time: '1 hour ago', read: true }
  ]);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Focus search input when search is toggled
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showSearch]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut for search (Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        onToggleSearch();
      }
      if (e.key === 'Escape' && showSearch) {
        onToggleSearch();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, onToggleSearch]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
    onToggleSearch();
    setSearchQuery('');
  };

  const handleNavClick = (target: string) => {
    onTabChange(target);
    setIsMobileMenuOpen(false);
    // Scroll to section
    const element = document.getElementById(target);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? isDarkMode
            ? 'bg-gray-900/95 backdrop-blur-md shadow-lg'
            : 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto max-w-7xl px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${
                isScrolled
                  ? isDarkMode
                    ? 'hover:bg-gray-800 text-white'
                    : 'hover:bg-gray-100 text-gray-700'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
            
            <a href="#home" className="flex items-center gap-3 group">
              {logo ? (
                <RoundedImage
                  imageUrl={logo}
                  className="w-8 h-8 md:w-10 md:h-10 border-2 border-white/20 group-hover:border-white/50 transition-colors"
                />
              ) : (
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-sm md:text-base">
                  {siteName.charAt(0)}
                </div>
              )}
              <span
                className={`font-primary font-bold text-lg md:text-xl transition-colors ${
                  isScrolled
                    ? isDarkMode
                      ? 'text-white'
                      : 'text-gray-800'
                    : 'text-white'
                }`}
              >
                {siteName}
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.target}
                onClick={() => handleNavClick(link.target)}
                className={`px-4 py-2 rounded-xl font-primary text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === link.target
                    ? isScrolled
                      ? isDarkMode
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-blue-500 text-white'
                      : 'bg-white/20 text-white backdrop-blur-sm'
                    : isScrolled
                    ? isDarkMode
                      ? 'text-gray-300 hover:bg-gray-800'
                      : 'text-gray-700 hover:bg-gray-100'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {getIcon(link.icon)}
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right Controls */}
          <div className="flex items-center gap-2">
            {/* Search Button */}
            <button
              onClick={onToggleSearch}
              className={`p-2 rounded-lg transition-colors ${
                isScrolled
                  ? isDarkMode
                    ? 'text-gray-300 hover:bg-gray-800'
                    : 'text-gray-600 hover:bg-gray-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              aria-label="Search"
            >
              <FaSearch size={18} />
              <span className="hidden lg:inline text-xs font-mono ml-1 opacity-60">⌘K</span>
            </button>

            {/* Theme Toggle */}
            <button
              onClick={onToggleTheme}
              className={`p-2 rounded-lg transition-colors ${
                isScrolled
                  ? isDarkMode
                    ? 'text-gray-300 hover:bg-gray-800'
                    : 'text-gray-600 hover:bg-gray-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              aria-label="Toggle theme"
            >
              {isDarkMode ? <FaSun size={18} /> : <FaMoon size={18} />}
            </button>

            {/* Bookmarks */}
            <button
              onClick={() => handleNavClick('bookmarks')}
              className={`p-2 rounded-lg transition-colors relative ${
                isScrolled
                  ? isDarkMode
                    ? 'text-gray-300 hover:bg-gray-800'
                    : 'text-gray-600 hover:bg-gray-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
              aria-label="Bookmarks"
            >
              <FaBookmark size={18} />
              {bookmarksCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                  {bookmarksCount}
                </span>
              )}
            </button>

            {/* Notifications */}
            <div ref={notificationRef} className="relative">
              <button
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className={`p-2 rounded-lg transition-colors relative ${
                  isScrolled
                    ? isDarkMode
                      ? 'text-gray-300 hover:bg-gray-800'
                      : 'text-gray-600 hover:bg-gray-100'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                aria-label="Notifications"
              >
                <FaBell size={18} />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-pulse">
                    {unreadNotifications}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {isNotificationsOpen && (
                <div className={`absolute right-0 mt-2 w-80 rounded-2xl shadow-2xl overflow-hidden ${
                  isDarkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <div className="flex items-center justify-between">
                      <h3 className={`font-primary font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Notifications
                      </h3>
                      {unreadNotifications > 0 && (
                        <button className="text-blue-500 text-sm font-medium hover:text-blue-600">
                          Mark all read
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifications.map(notification => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} hover:${
                          isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
                        } transition-colors cursor-pointer ${
                          !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <p className={`font-primary text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {notification.title}
                        </p>
                        <p className={`font-secondary text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                          {notification.time}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className={`p-3 text-center border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <button className="text-blue-500 text-sm font-medium hover:text-blue-600">
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div ref={userMenuRef} className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`flex items-center gap-2 p-1.5 rounded-xl transition-colors ${
                  isScrolled
                    ? isDarkMode
                      ? 'hover:bg-gray-800'
                      : 'hover:bg-gray-100'
                    : 'hover:bg-white/10'
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold text-sm">
                  {userAvatar ? (
                    <img src={userAvatar} alt={userName} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    userName.charAt(0).toUpperCase()
                  )}
                </div>
                <span className={`hidden md:block font-primary text-sm font-medium ${
                  isScrolled
                    ? isDarkMode
                      ? 'text-white'
                      : 'text-gray-800'
                    : 'text-white'
                }`}>
                  {userName}
                </span>
                <FaChevronDown className={`text-xs ${
                  isScrolled
                    ? isDarkMode
                      ? 'text-gray-400'
                      : 'text-gray-500'
                    : 'text-white/60'
                } ${isUserMenuOpen ? 'rotate-180' : ''} transition-transform`} />
              </button>

              {/* User Dropdown */}
              {isUserMenuOpen && (
                <div className={`absolute right-0 mt-2 w-56 rounded-2xl shadow-2xl overflow-hidden ${
                  isDarkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                    <p className={`font-primary font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {userName}
                    </p>
                    <p className="font-secondary text-xs text-gray-500">Free Account</p>
                  </div>
                  <div className="py-2">
                    {userMenu.showLogin && (
                      <button className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                      }`}>
                        <FaSignInAlt className="text-blue-500" />
                        <span className="font-primary text-sm font-medium">Sign In</span>
                      </button>
                    )}
                    {userMenu.showRegister && (
                      <button className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                        isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                      }`}>
                        <FaUserPlus className="text-green-500" />
                        <span className="font-primary text-sm font-medium">Create Account</span>
                      </button>
                    )}
                    <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} my-1`} />
                    <button className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                      isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}>
                      <FaBookOpen className="text-purple-500" />
                      <span className="font-primary text-sm font-medium">Reading History</span>
                    </button>
                    <button className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                      isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}>
                      <FaBookmark className="text-yellow-500" />
                      <span className="font-primary text-sm font-medium">Bookmarks</span>
                    </button>
                    <button className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                      isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'
                    }`}>
                      <FaCog className="text-gray-500" />
                      <span className="font-primary text-sm font-medium">Settings</span>
                    </button>
                    <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} my-1`} />
                    <button className={`w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors ${
                      isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-500 hover:bg-gray-50'
                    }`}>
                      <FaSignOutAlt />
                      <span className="font-primary text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search Bar - Expanded */}
        {showSearch && (
          <div className="py-4 animate-slideDown">
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for news, topics, providers..."
                className={`w-full px-5 py-3 rounded-xl border-2 focus:outline-none focus:ring-4 transition-all ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20'
                    : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20'
                }`}
              />
              <button
                type="submit"
                className="absolute right-3 top-1/2 -translate-y-1/2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors text-sm"
              >
                Search
              </button>
              <button
                type="button"
                onClick={onToggleSearch}
                className="absolute right-20 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes size={18} />
              </button>
            </form>
            <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
              <span>Popular:</span>
              <button className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                Politics
              </button>
              <button className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                Environment
              </button>
              <button className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                Sports
              </button>
              <button className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                Economy
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        className={`md:hidden fixed inset-x-0 top-16 bg-white dark:bg-gray-900 shadow-2xl transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="p-4 space-y-1">
          {navLinks.map((link) => (
            <button
              key={link.target}
              onClick={() => handleNavClick(link.target)}
              className={`w-full px-4 py-3 rounded-xl text-left font-primary font-medium flex items-center gap-3 transition-colors ${
                activeTab === link.target
                  ? 'bg-blue-500 text-white'
                  : isDarkMode
                  ? 'text-gray-300 hover:bg-gray-800'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {getIcon(link.icon)}
              {link.label}
            </button>
          ))}
          <div className={`border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'} my-2 pt-2`}>
            <button
              onClick={() => {
                onToggleTheme();
                setIsMobileMenuOpen(false);
              }}
              className={`w-full px-4 py-3 rounded-xl text-left flex items-center gap-3 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}
            >
              {isDarkMode ? <FaSun /> : <FaMoon />}
              {isDarkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}